/**
 * computeBadgePlacement — pure, dependency-free, obstacle-aware placement for a
 * Learn-Mode "?" badge relative to the `[data-help-context]` element it
 * annotates. Opt-in via `LearnModeOverlayProps.avoidObstacles`.
 *
 * Upstreamed 2026-10-16 from the social SPA's `LearnTipsOverlay` (where it was
 * added after live QA measured 8 collisions on 9 inbox badges and up to 21 in
 * the editor AI panel). The DOM-side obstacle check (`createRectFreeChecker` in
 * `./collectObstacleRects.ts`) is injected as a plain predicate, so this module
 * never touches `document`/`window`.
 *
 * Why per-candidate checking: a pre-gathered "obstacles near this one target"
 * set goes stale the moment a candidate moves elsewhere — dense sibling rows
 * (a header's search box / switcher / bell / avatar sit ~8px apart) made a badge
 * rejected from one corner fall through to a fixed offset that painted over the
 * previous neighbour. So each candidate rect is asked "is what is ACTUALLY
 * painted here free?".
 *
 * Resolution order (see `computeBadgePlacement`): 6 base candidates → the same 6
 * walked outward in `SEARCH_STEP` increments → a reduced-size badge at the
 * inside-top-right corner (only if free) → `null` (render NO badge rather than
 * ever covering a control).
 */

import type { Viewport } from "./geometry";

/** A plain rectangle — the shape `DOMRect` already has, kept separate so tests
 * never need a DOM. (Distinct from `RectLike` in `./geometry`, which has no
 * width/height.) */
export interface PlacementRect {
  top: number;
  left: number;
  right: number;
  bottom: number;
  width: number;
  height: number;
}

export interface BadgeSize {
  width: number;
  height: number;
}

/** Caller-supplied predicate: "is this exact rect clear of obstacles?" */
export type IsFree = (rect: PlacementRect) => boolean;

export type BadgePlacementStrategy =
  | "outside-top-right"
  | "outside-right-middle"
  | "outside-bottom-right"
  | "outside-top-left"
  | "outside-bottom-left"
  | "outside-left"
  | "inside-top-right-reduced";

export interface BadgePlacementResult {
  top: number;
  left: number;
  /** Which candidate won — exposed for tests/diagnostics, not rendered. */
  placement: BadgePlacementStrategy;
  /** True only for the last-resort `inside-top-right-reduced` spot — the caller
   * must render a SMALLER badge there (free-ness was checked at the reduced
   * size, not the normal one). */
  reduced: boolean;
}

/** Gap (px) kept between a badge and the edge it sits outside of. */
const DEFAULT_GAP = 6;

/** Outward search step/ceiling (px) for the "nothing free among the 6 base
 * candidates" fallback. */
const SEARCH_STEP = 4;
const MAX_SEARCH_RADIUS = 32;

/** Scale of the last-resort inside-the-corner badge (20px -> 16px). Exported so
 * the overlay sizes the reduced badge to match what was checked. */
export const REDUCED_SIZE_SCALE = 0.8;

function toRect(top: number, left: number, size: BadgeSize): PlacementRect {
  return { top, left, right: left + size.width, bottom: top + size.height, width: size.width, height: size.height };
}

function rectsOverlap(a: PlacementRect, b: PlacementRect): boolean {
  return a.left < b.right && a.right > b.left && a.top < b.bottom && a.bottom > b.top;
}

/** Test/convenience helper: builds an `IsFree` predicate from a plain list of
 * obstacle rects (strict AABB overlap — touching edges don't count). */
export function isFreeFromObstacles(obstacles: readonly PlacementRect[]): IsFree {
  return (rect) => !obstacles.some((obstacle) => rectsOverlap(rect, obstacle));
}

function isWithinViewport(r: PlacementRect, viewport: Viewport): boolean {
  return r.left >= 0 && r.top >= 0 && r.right <= viewport.width && r.bottom <= viewport.height;
}

/** A target with zero area, or entirely outside the viewport (scrolled away,
 * `display: none`, a collapsed panel), gets no badge at all. */
function isTargetVisible(targetRect: PlacementRect, viewport: Viewport): boolean {
  if (targetRect.width <= 0 || targetRect.height <= 0) return false;
  if (targetRect.right <= 0 || targetRect.left >= viewport.width) return false;
  if (targetRect.bottom <= 0 || targetRect.top >= viewport.height) return false;
  return true;
}

interface Candidate {
  top: number;
  left: number;
  placement: Exclude<BadgePlacementStrategy, "inside-top-right-reduced">;
}

/**
 * The 6 candidate spots, each pushed `extra` px further from the target along
 * its own outward direction (0 for the base spot). Order is priority: straddle
 * top-right first, then around the other corners/edges, outside-left last.
 */
function buildCandidates(targetRect: PlacementRect, badgeSize: BadgeSize, gap: number, extra: number): Candidate[] {
  const half = extra / 2;
  return [
    {
      top: targetRect.top - badgeSize.height / 2 - half,
      left: targetRect.right - badgeSize.width / 2 + half,
      placement: "outside-top-right",
    },
    {
      top: targetRect.top + targetRect.height / 2 - badgeSize.height / 2,
      left: targetRect.right + gap + extra,
      placement: "outside-right-middle",
    },
    {
      top: targetRect.bottom - badgeSize.height / 2 + half,
      left: targetRect.right - badgeSize.width / 2 + half,
      placement: "outside-bottom-right",
    },
    {
      top: targetRect.top - badgeSize.height / 2 - half,
      left: targetRect.left - badgeSize.width / 2 - half,
      placement: "outside-top-left",
    },
    {
      top: targetRect.bottom - badgeSize.height / 2 + half,
      left: targetRect.left - badgeSize.width / 2 - half,
      placement: "outside-bottom-left",
    },
    {
      top: targetRect.top,
      left: targetRect.left - badgeSize.width - gap - extra,
      placement: "outside-left",
    },
  ];
}

function tryCandidatesAt(
  targetRect: PlacementRect,
  viewport: Viewport,
  isFree: IsFree,
  badgeSize: BadgeSize,
  gap: number,
  extra: number
): BadgePlacementResult | null {
  for (const candidate of buildCandidates(targetRect, badgeSize, gap, extra)) {
    const rect = toRect(candidate.top, candidate.left, badgeSize);
    if (!isWithinViewport(rect, viewport)) continue;
    if (!isFree(rect)) continue;
    return { top: candidate.top, left: candidate.left, placement: candidate.placement, reduced: false };
  }
  return null;
}

/**
 * Where a `badgeSize` badge should sit relative to `targetRect`, never covering
 * anything `isFree` reports as occupied, never leaving `viewport`:
 *
 *  1. The 6 base candidates, right at the target's edge.
 *  2. The same 6 directions walked outward in `SEARCH_STEP` increments up to
 *     `MAX_SEARCH_RADIUS` — nearest radius wins across all 6 before a farther
 *     one is tried.
 *  3. A reduced-size badge at the inside-top-right corner — ONLY if that
 *     smaller footprint is itself free.
 *  4. `null` — no safe spot; render no badge for this anchor.
 */
export function computeBadgePlacement(
  targetRect: PlacementRect,
  viewport: Viewport,
  isFree: IsFree,
  badgeSize: BadgeSize,
  gap: number = DEFAULT_GAP
): BadgePlacementResult | null {
  if (!isTargetVisible(targetRect, viewport)) return null;

  const atEdge = tryCandidatesAt(targetRect, viewport, isFree, badgeSize, gap, 0);
  if (atEdge) return atEdge;

  for (let extra = SEARCH_STEP; extra <= MAX_SEARCH_RADIUS; extra += SEARCH_STEP) {
    const found = tryCandidatesAt(targetRect, viewport, isFree, badgeSize, gap, extra);
    if (found) return found;
  }

  const reducedSize: BadgeSize = {
    width: badgeSize.width * REDUCED_SIZE_SCALE,
    height: badgeSize.height * REDUCED_SIZE_SCALE,
  };
  const insideTop = targetRect.top + gap;
  const insideLeft = targetRect.right - reducedSize.width - gap;
  const insideRect = toRect(insideTop, insideLeft, reducedSize);
  if (isWithinViewport(insideRect, viewport) && isFree(insideRect)) {
    return { top: insideTop, left: insideLeft, placement: "inside-top-right-reduced", reduced: true };
  }

  return null;
}
